import React from 'react'
import { Separator, useAtSize, colors, Ellipsis } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Link } from '@fs/zion-router'
import { boolAttr } from '@fs/zion-frontend-friends'
import { format } from '@fs/zion-locale/date-fns'
import { useUser } from '@fs/zion-user'
import PillButton from '../PillButton'
import { H5, H6 } from '../type/type'
import AddToPlaylistButton from '../AddToPlaylistButton'

const imgStyle = css`
  height: 180px;
  width: 180px;
  object-fit: cover;
  border-radius: 12px;
`
const actionButtonContainerStyle = css`
  display: flex;
  align-self: center;
  flex-direction: column;
  height: 100%;
  align-items: flex-end;
  &[is-mobile] {
    align-items: flex-start;
  }
  & > button {
    margin-top: 16px;
  }
`
const mainStyle = css`
  &[is-selected] {
    border-radius: 16px;
    background: linear-gradient(90.52deg, #00b1cf 0%, #7fc350 100%);
  }

  &:not([is-selected]) {
    border-bottom: 1px solid #3d3d3d;
  }
`
const mainRowStyle = css`
  justify-content: space-around;
  padding: 24px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: 32px;
  align-items: start;
`
const mobileWrapperCss = css`
  display: grid;
  grid-row-gap: 12px;
  justify-items: start;

  & + & {
    margin-top: 24px;
  }
`
const mobileHeaderWrapperCss = css`
  align-items: center;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 2fr 5fr;
`
const mobileImageCss = css`
  border-radius: 4px;
  height: auto;
  object-fit: cover;
  object-position: center center;
  width: 100%;
  width: 111px;
  height: 80px;
`
const mobileHeaderCss = css`
  display: grid;
  grid-row-gap: 8px;
`
const mobileHeadingCss = css`
  font-size: 16px;
  line-height: 18px;
  font-weight: 600;
`
const mobileSubheadingCss = css`
  font-size: 14px;
  line-height: 18px;
  color: ${colors.billboard.gray20};
`
const mobileDetailCss = css`
  color: ${colors.billboard.gray20};
  margin: 4px 0 8px 0;
  max-width: 450px;
  font-size: 14px;
  line-height: 20px;
`

const tagCss = css`
  border-radius: 2px;
  display: inline-block;
  padding: 2px 7px;
  text-transform: uppercase;
  font-size: 12px;
`

const languageTagCss = css`
  background: var(--background-secondary);
  color: #fff;
  font-weight: 600;
  ${tagCss}
  position: absolute;
  bottom: 16px;
  left: 16px;

  &[is-mobile] {
    bottom: 5px;
    left: 0;
  }
`
const liveTagCss = css`
  color: #270d1b;
  background: #fc4e6d;
  font-weight: 600;
  ${tagCss}
  position: absolute;
  bottom: 40px;
  left: 16px;
  &[is-mobile] {
    font-weight: 400;
    font-size: 10px;
    bottom: 25px;
    left: 0;
  }
`
const goLiveCss = css`
  color: #fff;
  font-weight: 600;
  ${tagCss}
  position: absolute;
  bottom: 2px;
  left: 16px;
  font-size: 10px;
  &[is-mobile] {
    font-weight: 400;
    font-size: 8px;
    bottom: 25px;
    left: 0;
  }
`

const imageWrapperCss = css`
  position: relative;
`

const SeriesVideoRow = ({
  image,
  spokenLanguage,
  headingText,
  detailText,
  presenterText,
  isInPlaylist,
  contentUUID,
  url,
  isLive,
  goLivetime,
}) => {
  const user = useUser()
  const [t] = useTranslation()
  const atSize = useAtSize()
  const isMobile = atSize({ default: true, md: false })
  if (isMobile) {
    return (
      <div css={mobileWrapperCss}>
        <Separator size="xxs" />
        <Link noLinkStyles to={url}>
          <div css={mobileHeaderWrapperCss}>
            <div css={imageWrapperCss}>
              <img src={image} css={mobileImageCss} alt="" aria-hidden />
              {spokenLanguage && (
                <div css={languageTagCss} is-mobile={boolAttr(isMobile)}>
                  {spokenLanguage}
                </div>
              )}

              {isLive && (
                <div css={liveTagCss} is-mobile={boolAttr(isMobile)}>
                  {t('card.tag.live', 'Live')}
                </div>
              )}
            </div>
            <div css={mobileHeaderCss}>
              <H5>
                <span css={mobileHeadingCss}>{headingText}</span>
              </H5>
              <H6>
                <span css={mobileSubheadingCss}>{presenterText}</span>
              </H6>
            </div>
          </div>
        </Link>
        <Link noLinkStyles to={url}>
          <div css={mobileDetailCss}>
            <Ellipsis lineClamp={5}>{detailText} </Ellipsis>
          </div>
        </Link>
        <div css={actionButtonContainerStyle} is-mobile={boolAttr(isMobile)}>
          <PillButton data-testid="watch" to={url}>
            {t('series.view.action', 'View Session')}
          </PillButton>
          {user.signedIn && <AddToPlaylistButton isInPlaylist={isInPlaylist} contentUUID={contentUUID} />}
        </div>
      </div>
    )
  }
  return (
    <div css={mainStyle}>
      <Separator />
      <div css={mainRowStyle}>
        <Link noLinkStyles to={url}>
          <div css={imageWrapperCss}>
            <img src={image} css={imgStyle} alt="" aria-hidden />
            {spokenLanguage && <div css={languageTagCss}>{spokenLanguage}</div>}
            {isLive && (
              <div css={liveTagCss} is-mobile={boolAttr(isMobile)}>
                {t('card.tag.live', 'Live')}
              </div>
            )}

            {isLive && goLivetime && (
              <>
                <div css={liveTagCss} is-mobile={boolAttr(isMobile)}>
                  {t('card.tag.live', 'Live')}
                </div>
                <div css={goLiveCss} is-mobile={boolAttr(isMobile)}>
                  {format(new Date(goLivetime), 'Pp')}
                </div>
              </>
            )}
          </div>
        </Link>
        <Link noLinkStyles to={url}>
          <div style={{ maxWidth: 500 }}>
            <H5 as="h3">
              <div style={{ fontWeight: '700' }}>{headingText}</div>
            </H5>
            <Separator size="xs" />
            <H6 as="h4">{presenterText}</H6>
            <Separator size="xs" />
            <div>
              <Ellipsis lineClamp={5}>{detailText} </Ellipsis>
            </div>
          </div>
        </Link>
        <div css={actionButtonContainerStyle}>
          <PillButton data-testid="watch" to={url}>
            {t('series.view.action', 'View Session')}
          </PillButton>
          {user.signedIn && <AddToPlaylistButton isInPlaylist={isInPlaylist} contentUUID={contentUUID} />}
        </div>
      </div>
      <Separator />
    </div>
  )
}

export default SeriesVideoRow
